import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle, Info, ChevronDown, ExternalLink, Share2, Copy, Check, Printer, RotateCw } from "lucide-react";
import FullAuditLeadDialog from "@/components/FullAuditLeadDialog";

const SCORE_TIERS = [
  { min: 80, label: "EXCELLENT", bg: "bg-[#002FA7]", text: "text-white" },
  { min: 60, label: "GOOD", bg: "bg-[#0a0a0a]", text: "text-white" },
  { min: 40, label: "FAIR", bg: "bg-[#FFD700]", text: "text-[#0a0a0a]" },
  { min: 0, label: "POOR", bg: "bg-[#FF2400]", text: "text-white" },
];

const getScoreTier = (s) => {
  if (typeof s !== "number") return null;
  return SCORE_TIERS.find((t) => s >= t.min) || SCORE_TIERS[SCORE_TIERS.length - 1];
};

const VERDICT_STYLES = {
  PROCEED: {
    bg: "bg-[#002FA7]",
    text: "text-white",
    label: "PROCEED",
    icon: CheckCircle2,
    desc: "Pengalaman pengguna nyata mendukung klaim. Risiko rendah.",
  },
  CAUTION: {
    bg: "bg-[#FFD700]",
    text: "text-[#0a0a0a]",
    label: "CAUTION",
    icon: AlertTriangle,
    desc: "Ada diskrepansi antara klaim & realita. Pertimbangkan dengan hati-hati.",
  },
  AVOID: {
    bg: "bg-[#FF2400]",
    text: "text-white",
    label: "AVOID",
    icon: XCircle,
    desc: "Keluhan pengguna konsisten. Risiko tinggi.",
  },
  INSUFFICIENT_DATA: {
    bg: "bg-[#666666]",
    text: "text-white",
    label: "INSUFFICIENT DATA",
    icon: Info,
    desc: "Data organik tidak memadai untuk memberikan verdict.",
  },
};

export default function AuditReport({ report, onForceReaudit, forceGate = false }) {
  if (!report) return null;

  // Premium gating logic:
  // - forceGate=true → always gate (e.g. shared /a/{id} viewer)
  // - report.tier !== 'premium' → user is on Free tier, gate premium sections
  const isPremiumGated = forceGate || report.tier !== "premium";

  // Clarification case
  if (report.needs_clarification) {
    return (
      <div className="border-2 border-black bg-[#FFD700] p-6 md:p-8" data-testid="audit-clarification">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]">[ CLARIFICATION REQUIRED ]</div>
        <div className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mt-2">
          Konfirmasi Kategori Dulu
        </div>
        <p className="mt-3 font-sans text-base">{report.clarification_question}</p>
        {report.suggested_categories && report.suggested_categories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {report.suggested_categories.map((c, i) => (
              <span key={i} className="border-2 border-black bg-white px-3 py-1.5 font-mono text-xs uppercase tracking-[0.16em]" data-testid={`suggested-cat-${i}`}>
                {c}
              </span>
            ))}
          </div>
        )}
        <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.16em]">
          Tip: tulis kategori pada field <span className="underline">Kategori</span> di form, lalu jalankan audit ulang.
        </div>
      </div>
    );
  }

  const verdictKey = report.verdict || "INSUFFICIENT_DATA";
  const v = VERDICT_STYLES[verdictKey] || VERDICT_STYLES.INSUFFICIENT_DATA;
  const VIcon = v.icon;
  const stamp = new Date(report.timestamp).toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  // Audit-ulang behavior:
  // - On landing page (where onForceReaudit is provided) -> trigger force re-audit in-place
  // - On shared /a/{id} page (no callback) -> navigate to home with query params for autorun
  const handleReaudit = () => {
    if (typeof onForceReaudit === "function") {
      onForceReaudit();
      return;
    }
    const params = new URLSearchParams({
      brand: report.brand || "",
      model: report.model || "",
      autorun: "1",
    });
    if (report.category) params.set("category", report.category);
    if (report.tier) params.set("tier", report.tier);
    window.location.href = `/?${params.toString()}#audit`;
  };

  return (
    <article className="bg-white border-2 border-black" data-testid="audit-report">
      {/* HEADER */}
      <div className="border-b-2 border-black p-5 md:p-6 grid md:grid-cols-12 gap-4 items-end">
        <div className="md:col-span-8">
          <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#666666]">
            Executive Audit Report
          </div>
          <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl leading-[0.95] mt-1" data-testid="report-title">
            {report.brand} {report.model}
          </h3>
          {report.category && (
            <div className="mt-2 inline-block border-2 border-black px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]" data-testid="report-category">
              KATEGORI: {report.category}
            </div>
          )}
        </div>
        <div className="md:col-span-4 md:text-right font-mono text-[11px] uppercase tracking-[0.16em] text-[#666666] space-y-1">
          <div>ID: {report.id?.slice(0, 8).toUpperCase()}</div>
          <div>STAMP: {stamp}</div>
          <div>MARKET: ID · 12M</div>
          {report.cached && (
            <div className="text-[#FFD700] bg-black px-2 py-1 inline-block" data-testid="report-cached-badge">
              ⚡ CACHED · INSTANT
            </div>
          )}
          {report.data_source === "apify_serp" && report.scraped_counts && (
            <div className="text-[#002FA7] font-bold" data-testid="report-data-source">
              SOURCE: APIFY · {Object.values(report.scraped_counts).reduce((a, b) => a + (b || 0), 0)} HITS
            </div>
          )}
          {report.data_source === "knowledge_only" && (
            <div className="text-[#666666]" data-testid="report-data-source">
              SOURCE: PI KNOWLEDGE (NO LIVE SCRAPE)
            </div>
          )}
        </div>
      </div>

      {/* VERDICT */}
      <div className={`${v.bg} ${v.text} p-8 md:p-12 border-b-2 border-black`} data-testid="report-verdict-block">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.24em] opacity-90">
            The Verdict
          </div>
          <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.16em] opacity-90">
            // Rendered on real-world signal
          </div>
        </div>
        <div className="mt-3 flex items-center gap-5">
          <VIcon className="w-12 h-12 md:w-16 md:h-16" strokeWidth={2.5} />
          <div className="font-display font-black uppercase tracking-tighter text-5xl md:text-8xl leading-none" data-testid="report-verdict-label">
            {v.label}
          </div>
        </div>
        <div className="mt-4 max-w-2xl font-sans text-sm md:text-base">{v.desc}</div>
      </div>

      {/* OVERALL SCORE */}
      <OverallScore score={report.overall_score} verdict={verdictKey} />

      {/* SOURCES GRID */}
      <div className="border-b-2 border-black">
        <div className="px-5 md:px-6 py-3 border-b-2 border-black font-mono text-[10px] uppercase tracking-[0.2em] flex items-center justify-between">
          <span>01 · Ringkasan Sumber &amp; Sentimen</span>
          <span className="text-[#666666]">N = {report.sources?.length || 0}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-collapse">
          {(report.sources || []).map((s, i) => (
            <SourceCell
              key={i}
              idx={i}
              source={s}
              total={report.sources.length}
              scrapedCount={report.scraped_counts ? report.scraped_counts[s.platform] : undefined}
            />
          ))}
          {(!report.sources || report.sources.length === 0) && (
            <div className="col-span-2 md:col-span-4 p-6 font-mono text-xs uppercase tracking-[0.18em] text-[#666666]">
              Tidak ada data sumber.
            </div>
          )}
        </div>
      </div>

      {/* REALITY CHECK */}
      <Section label="02 · The Reality Check">
        <p className="font-mono text-base md:text-lg leading-snug" data-testid="report-reality-check">
          {report.reality_check || "—"}
        </p>
      </Section>

      {/* DEAL BREAKER */}
      <Section label="03 · The Deal Breaker" warning>
        <p className="font-sans text-base md:text-lg leading-relaxed" data-testid="report-deal-breaker">
          {report.deal_breaker || "—"}
        </p>
      </Section>

      {/* RISK */}
      <Section label="04 · Risk Assessment">
        <p className="font-sans text-base leading-relaxed" data-testid="report-risk">
          {report.risk_assessment || "—"}
        </p>
      </Section>

      {/* ACTIONABLE ADVICE */}
      <Section label="05 · Actionable Advice">
        <p className="font-sans text-base leading-relaxed" data-testid="report-advice">
          {report.actionable_advice || "—"}
        </p>
      </Section>

      {/* PREMIUM-ONLY SECTIONS */}
      {isPremiumGated ? (
        <LockedPremiumTeaser
          product={[report.brand, report.model, report.category && `(${report.category})`].filter(Boolean).join(" ")}
        />
      ) : (
        <PremiumSections report={report} />
      )}

      {/* SCRAPED SOURCES (raw evidence) */}
      {report.scraped_data && Object.keys(report.scraped_data).length > 0 && (
        <ScrapedEvidence data={report.scraped_data} />
      )}

      {/* DYNAMIC DISCLAIMER */}
      <div
        className="border-t-2 border-black bg-[#fff8d6] p-5 md:p-6"
        data-testid="audit-dynamic-disclaimer"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0a0a0a] flex items-center gap-2">
          <Info className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span>Catatan · Hasil Bersifat Dinamis</span>
        </div>
        <p className="mt-2 font-sans text-sm md:text-[15px] leading-relaxed text-[#0a0a0a]">
          Setiap hasil audit bersifat <span className="font-bold">dinamis</span>. Skor dan hasil akhir (verdict) sangat bergantung pada waktu pelaksanaan audit. Ulasan terbaru di platform seperti YouTube, TikTok, Shopee, Tokopedia, maupun berbagai forum komunitas dapat memengaruhi hasil audit berikutnya. Jadikan hasil ini sebagai <span className="font-bold">snapshot</span> (gambaran sesaat) pada waktu tertentu, bukan sebagai kebenaran absolut. Disarankan untuk melakukan audit secara berkala guna memperoleh data dan sinyal konsumen terbaru.
        </p>
        <p className="mt-3 font-sans text-sm md:text-[15px] leading-relaxed text-[#0a0a0a]">
          Laporan ini dihasilkan oleh PI (Product Intelligence) berdasarkan analisis sinyal publik seperti ulasan konsumen, diskusi online, marketplace, media sosial, dan forum komunitas dalam periode analisis tertentu. Seluruh hasil, skor, kesimpulan, dan verdict merupakan <span className="font-bold">interpretasi berbasis pola data publik</span> dan bukan merupakan sertifikasi resmi, pengujian laboratorium, endorsement, ataupun jaminan kualitas produk tertentu.
        </p>
        <div className="mt-4">
          <button
            type="button"
            onClick={handleReaudit}
            className="inline-flex items-center gap-2 border-2 border-black bg-[#0a0a0a] text-[#FFD700] px-4 py-2.5 font-mono font-bold uppercase tracking-[0.18em] text-xs md:text-sm hover:bg-[#FFD700] hover:text-black transition-colors"
            data-testid="reaudit-now-button"
          >
            <RotateCw className="w-4 h-4" strokeWidth={2.5} />
            Audit Ulang Sekarang
          </button>
        </div>
      </div>

      {/* FOOTER STAMP */}
      <div className="p-5 md:p-6 border-t-2 border-dashed border-black flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.2em]">
        <span>END OF REPORT</span>
        <div className="flex items-center gap-2 flex-wrap">
          <ShareButtons report={report} verdict={v.label} />
          {report.cached && onForceReaudit && (
            <button
              type="button"
              onClick={onForceReaudit}
              className="border-2 border-black bg-[#FFD700] px-3 py-1.5 hover:bg-black hover:text-[#FFD700] transition-colors"
              data-testid="force-reaudit-button"
            >
              ↻ Audit Ulang
            </button>
          )}
          <span className="text-[#666]">SIGNED // CHIEF AUDITOR · PI</span>
        </div>
      </div>
    </article>
  );
}

function OverallScore({ score, verdict }) {
  const hasScore = typeof score === "number";
  const tier = hasScore ? getScoreTier(score) : null;
  return (
    <div className="border-b-2 border-black bg-white" data-testid="overall-score-block">
      <div className="px-5 md:px-6 py-3 border-b-2 border-black font-mono text-[10px] uppercase tracking-[0.2em] flex items-center justify-between">
        <span>00 · Overall Audit Score</span>
        <span className="text-[#666666]">/ skala 0 – 100</span>
      </div>
      <div className="grid grid-cols-12">
        <div className={`col-span-12 md:col-span-5 ${tier ? tier.bg : "bg-[#e9e9e9]"} ${tier ? tier.text : "text-[#666]"} p-6 md:p-8 md:border-r-2 border-black border-b-2 md:border-b-0 border-black flex flex-col justify-center`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">
            Score
          </div>
          <div
            className="font-display font-black tracking-tighter leading-none text-7xl md:text-[140px] mt-1"
            data-testid="overall-score-value"
          >
            {hasScore ? score : "—"}
            {hasScore && (
              <span className="font-display text-3xl md:text-5xl opacity-60 align-top ml-1">/100</span>
            )}
          </div>
          {tier && (
            <div className="mt-3 inline-block border-2 border-current px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.22em] font-bold w-fit" data-testid="overall-score-label">
              {tier.label}
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-7 p-6 md:p-8 flex flex-col justify-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#666666]">
            Skala Penilaian
          </div>
          <ul className="mt-3 grid grid-cols-1 gap-2 font-sans text-sm">
            <ScoreScaleRow color="bg-[#002FA7]" label="80 – 100" name="EXCELLENT" desc="Klaim mendukung realita, risiko minor." active={tier?.label === "EXCELLENT"} />
            <ScoreScaleRow color="bg-[#0a0a0a]" label="60 – 79" name="GOOD" desc="Layak, dengan trade-off jelas." active={tier?.label === "GOOD"} />
            <ScoreScaleRow color="bg-[#FFD700]" textColor="text-[#0a0a0a]" label="40 – 59" name="FAIR" desc="Diskrepansi signifikan, pertimbangkan ulang." active={tier?.label === "FAIR"} />
            <ScoreScaleRow color="bg-[#FF2400]" label="0 – 39" name="POOR" desc="Keluhan fatal konsisten, hindari." active={tier?.label === "POOR"} />
          </ul>
          {!hasScore && (
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#666666]">
              {verdict === "INSUFFICIENT_DATA"
                ? "* Skor tidak dihitung — data organik tidak memadai."
                : "* Audit lama — skor belum tersedia."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScoreScaleRow({ color, textColor = "text-white", label, name, desc, active }) {
  return (
    <li className={`flex items-center gap-3 ${active ? "opacity-100" : "opacity-60"}`}>
      <span className={`inline-flex items-center justify-center ${color} ${textColor} border-2 border-black font-mono text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 min-w-[78px]`}>
        {label}
      </span>
      <span className={`font-mono text-[11px] uppercase tracking-[0.22em] font-bold min-w-[80px] ${active ? "underline" : ""}`}>
        {name}
      </span>
      <span className="font-sans text-sm text-[#333]">{desc}</span>
    </li>
  );
}

function SourceCell({ idx, source, total, scrapedCount }) {
  const isRightEdge = (idx + 1) % 4 === 0;
  const isLastRow = idx >= Math.floor((total - 1) / 4) * 4;
  const sentimentColor =
    source.sentiment === "Positif"
      ? "bg-[#002FA7] text-white"
      : source.sentiment === "Negatif"
        ? "bg-[#FF2400] text-white"
        : source.sentiment === "Mixed"
          ? "bg-[#FFD700] text-[#0a0a0a]"
          : "bg-[#666666] text-white";
  return (
    <div
      className={`p-4 md:p-5 ${!isRightEdge ? "md:border-r-2 md:border-black" : ""} ${idx % 2 === 0 ? "border-r-2 border-black md:border-r-2" : ""} ${!isLastRow ? "border-b-2 border-black" : ""}`}
      data-testid={`source-cell-${idx}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666666] flex items-center justify-between">
        <span>{source.platform}</span>
        {typeof scrapedCount === "number" && (
          <span className="font-bold text-[#002FA7]">{scrapedCount} hits</span>
        )}
      </div>
      <div className="mt-2 font-display font-black uppercase tracking-tight text-xl md:text-2xl">
        {source.count_label}
      </div>
      <div className="mt-2 font-sans text-sm leading-snug text-[#0a0a0a]">
        {source.indication}
      </div>
      <div className={`mt-3 inline-block px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] border-2 border-black ${sentimentColor}`}>
        {source.sentiment}
      </div>
    </div>
  );
}

function Section({ label, children, warning, last }) {
  // Split label like "02 · The Reality Check" into index + title
  const m = typeof label === "string" ? label.match(/^(\d+)\s*[·\-—]\s*(.+)$/) : null;
  const index = m ? m[1] : null;
  const title = m ? m[2] : label;
  return (
    <div className={last ? "" : "border-b-2 border-black"}>
      <div className="px-5 md:px-7 py-5 md:py-6 border-b-2 border-black flex items-start justify-between gap-4">
        <div className="flex items-baseline gap-3 md:gap-4">
          {index && (
            <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#666] flex-shrink-0">
              {index}
            </span>
          )}
          <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-3xl leading-[1.05]">
            {title}
          </h3>
        </div>
        {warning && (
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF2400] mt-1.5 flex-shrink-0">CRITICAL</span>
        )}
      </div>
      <div className={`p-5 md:p-7 ${warning ? "bg-[#fff4f1] border-l-[6px] border-[#FF2400]" : ""}`}>
        {children}
      </div>
    </div>
  );
}

function LockedPremiumTeaser({ product = "" }) {  // eslint-disable-line no-unused-vars
  const handleUpgrade = () => {
    const formSection = document.querySelector("#top");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Trigger premium tier button click after scroll
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="tier-premium-button"]');
      if (btn) btn.click();
    }, 600);
  };
  const sections = [
    { idx: "06", title: "Pricing Analysis", desc: "Analisis posisi harga & value-for-money." },
    { idx: "07", title: "Use Case Fit", desc: "Cocok untuk siapa, hindari untuk siapa." },
    { idx: "08", title: "Competitor Comparison", desc: "Bandingkan langsung dengan kompetitor sekelas." },
    { idx: "09", title: "Recommended Alternatives", desc: "Alternatif lebih baik di kelas harga sama." },
    { idx: "10", title: "Longevity Score", desc: "Estimasi umur pemakaian & durabilitas." },
    { idx: "11", title: "After-Sales Signal", desc: "Sinyal garansi, sparepart, & service." },
  ];
  return (
    <div data-testid="locked-premium-teaser">
      <div className="bg-black text-[#FFD700] px-5 md:px-7 py-4 border-b-2 border-black font-mono text-[10px] md:text-xs uppercase tracking-[0.22em] flex items-center justify-between gap-3">
        <span>◆ Premium Intelligence · Full Audit</span>
        <span className="text-white">LOCKED</span>
      </div>
      <div className="relative">
        <div className="divide-y-2 divide-black filter blur-[6px] pointer-events-none select-none" aria-hidden="true">
          {sections.map((s) => (
            <div key={s.idx} className="p-5 md:p-7">
              <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#666]">{s.idx}</div>
              <div className="font-display font-black uppercase tracking-tight text-xl md:text-3xl mt-1">
                {s.title}
              </div>
              <p className="mt-2 font-sans text-base text-[#444]">{s.desc}</p>
              <div className="mt-3 h-3 bg-[#e5e5e5]" />
              <div className="mt-2 h-3 bg-[#e5e5e5] w-2/3" />
              <div className="mt-2 h-3 bg-[#e5e5e5] w-1/2" />
            </div>
          ))}
        </div>
        {/* CTA Overlay */}
        <div className="absolute inset-0 grid place-items-center bg-white/50 backdrop-blur-[2px] p-6">
          <div className="border-2 border-black bg-white p-6 md:p-8 max-w-md w-full shadow-brutal" data-testid="premium-cta-overlay">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">◆ Premium Content Locked</div>
            <h3 className="mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
              Buka 6 Section
              <br />
              <span className="text-[#666]">Premium.</span>
            </h3>
            <ul className="mt-4 space-y-1 text-sm font-sans">
              <li>· Pricing Analysis · Use Case Fit</li>
              <li>· Competitor Comparison · Alternatives</li>
              <li>· Longevity Score · After-Sales Signal</li>
            </ul>
            <div className="mt-5 border-2 border-black p-3 bg-[#FFD700]">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em]">Premium · 24 Jam</div>
              <div className="font-display font-black text-2xl mt-0.5">Rp 19.900</div>
            </div>
            <button
              type="button"
              onClick={handleUpgrade}
              className="mt-4 w-full bg-black text-[#FFD700] px-5 py-3 font-mono font-bold uppercase tracking-[0.18em] text-xs md:text-sm border-2 border-black hover:bg-[#FFD700] hover:text-black transition-colors"
              data-testid="upgrade-premium-button"
            >
              ▶ Upgrade ke Premium
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PremiumSections({ report }) {
  const ucf = report.use_case_fit || {};
  const comp = report.competitor_comparison || [];
  const alt = report.recommended_alternatives || [];
  return (
    <div data-testid="premium-sections">
      <div className="bg-black text-[#FFD700] px-5 md:px-6 py-3 border-b-2 border-black font-mono text-[10px] uppercase tracking-[0.22em] flex items-center justify-between">
        <span>◆ PREMIUM INTELLIGENCE · CLAUDE SONNET 4.5</span>
        <span className="text-white">Sonnet</span>
      </div>

      {report.pricing_analysis && (
        <Section label="06 · Pricing Analysis">
          <p className="font-sans text-base leading-relaxed" data-testid="report-pricing">
            {report.pricing_analysis}
          </p>
        </Section>
      )}

      {(ucf.ideal_for || ucf.avoid_for) && (
        <div className="border-b-2 border-black" data-testid="report-use-case">
          <div className="px-5 md:px-7 py-5 md:py-6 border-b-2 border-black flex items-baseline gap-3 md:gap-4">
            <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#666]">07</span>
            <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-3xl leading-[1.05]">Use Case Fit</h3>
          </div>
          <div className="grid md:grid-cols-2 divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
            <div className="p-5 md:p-7 bg-[#e8f0ff]">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7]">✓ Cocok untuk</div>
              <p className="mt-2 font-sans text-base leading-relaxed">{ucf.ideal_for || "—"}</p>
            </div>
            <div className="p-5 md:p-7 bg-[#fff4f1]">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF2400]">✗ Hindari untuk</div>
              <p className="mt-2 font-sans text-base leading-relaxed">{ucf.avoid_for || "—"}</p>
            </div>
          </div>
        </div>
      )}

      {comp.length > 0 && (
        <div className="border-b-2 border-black" data-testid="report-competitors">
          <div className="px-5 md:px-7 py-5 md:py-6 border-b-2 border-black flex items-baseline gap-3 md:gap-4">
            <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#666]">08</span>
            <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-3xl leading-[1.05]">Competitor Comparison</h3>
          </div>
          <div className="divide-y-2 divide-black">
            {comp.map((c, i) => (
              <div key={i} className="px-5 md:px-7 py-4 flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-bold text-lg uppercase tracking-tight">{c.name}</div>
                  <div className="mt-1 font-sans text-sm leading-snug text-[#444]">{c.edge}</div>
                </div>
                {c.price_range && (
                  <div className="font-mono text-xs uppercase tracking-[0.16em] border-2 border-black px-2 py-1 bg-[#FFD700]">
                    {c.price_range}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {alt.length > 0 && (
        <Section label="09 · Recommended Alternatives" last={false}>
          <ul className="space-y-2.5" data-testid="report-alternatives">
            {alt.map((a, i) => (
              <li key={i} className="border-l-4 border-[#002FA7] pl-3">
                <div className="font-display font-bold text-base">{a.name}</div>
                <div className="font-sans text-sm text-[#444] leading-snug">{a.reason}</div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(report.longevity_score || report.after_sales_signal) && (
        <div className="border-b-2 border-black grid md:grid-cols-2 divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
          {report.longevity_score && (
            <div data-testid="report-longevity">
              <div className="px-5 md:px-7 py-5 md:py-6 border-b-2 border-black flex items-baseline gap-3 md:gap-4">
                <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#666]">10</span>
                <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-3xl leading-[1.05]">Longevity Score</h3>
              </div>
              <div className="p-5 md:p-7">
                <p className="font-sans text-base leading-relaxed font-bold">{report.longevity_score}</p>
              </div>
            </div>
          )}
          {report.after_sales_signal && (
            <div data-testid="report-aftersales">
              <div className="px-5 md:px-7 py-5 md:py-6 border-b-2 border-black flex items-baseline gap-3 md:gap-4">
                <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#666]">11</span>
                <h3 className="font-display font-black uppercase tracking-tight text-xl md:text-3xl leading-[1.05]">After-Sales Signal</h3>
              </div>
              <div className="p-5 md:p-7">
                <p className="font-sans text-base leading-relaxed">{report.after_sales_signal}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScrapedEvidence({ data }) {
  const [open, setOpen] = useState(false);
  const total = Object.values(data).reduce((a, b) => a + (b?.length || 0), 0);
  const platforms = Object.keys(data);
  return (
    <div className="border-b-2 border-black" data-testid="scraped-evidence">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 md:px-6 py-3 border-b-2 border-black font-mono text-[10px] uppercase tracking-[0.2em] flex items-center justify-between hover:bg-yellow-50"
        data-testid="scraped-evidence-toggle"
      >
        <span>06 · Bukti Mentah Hasil Scraping ({total} URL)</span>
        <span className="flex items-center gap-2">
          <span className="text-[#666]">{open ? "Tutup" : "Lihat Sumber"}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="divide-y-2 divide-black">
          {platforms.map((p) => {
            const items = data[p] || [];
            if (items.length === 0) {
              return (
                <div key={p} className="px-5 md:px-6 py-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">{p}</div>
                  <div className="mt-1 font-sans text-sm text-[#666] italic">Tidak ada hasil organik.</div>
                </div>
              );
            }
            return (
              <div key={p} className="px-5 md:px-6 py-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666]">
                  {p} · <span className="text-[#002FA7] font-bold">{items.length} hasil</span>
                </div>
                <ul className="mt-3 space-y-3">
                  {items.map((it, i) => (
                    <li key={i} className="border-l-2 border-black pl-3" data-testid={`scraped-item-${p}-${i}`}>
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-display font-bold text-base leading-tight hover:underline inline-flex items-start gap-1.5"
                      >
                        {it.title || "(no title)"}
                        <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      </a>
                      {it.description && (
                        <div className="mt-1 font-sans text-sm text-[#444] leading-snug">{it.description}</div>
                      )}
                      {it.url && (
                        <div className="mt-1 font-mono text-[10px] text-[#888] truncate">{it.url}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ShareButtons({ report, verdict }) {
  const [copied, setCopied] = useState(false);

  const buildText = () => {
    const shareUrl = report.id
      ? `${window.location.origin}/a/${report.id}`
      : window.location.origin;
    const scoreLine = typeof report.overall_score === "number"
      ? `🎯 Overall Score: ${report.overall_score}/100`
      : null;
    const lines = [
      `[VERDICT: ${verdict}] ${report.brand} ${report.model}${report.category ? ` (${report.category})` : ""}`,
      scoreLine,
      "",
      `📊 Reality Check: ${report.reality_check || "-"}`,
      "",
      `⚠️ Deal Breaker: ${report.deal_breaker || "-"}`,
      "",
      `📍 Risiko: ${report.risk_assessment || "-"}`,
      "",
      `✅ Saran: ${report.actionable_advice || "-"}`,
      "",
      `🔍 Audit lengkap: ${shareUrl}`,
      `Diaudit oleh FAKTA PRODUK — Chief Auditor untuk konsumen Indonesia.`,
    ].filter((l) => l !== null);
    return lines.join("\n");
  };

  const shareWA = () => {
    const text = encodeURIComponent(buildText());
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = buildText();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div className="flex items-center gap-2 print-hide">
      <button
        type="button"
        onClick={shareWA}
        className="border-2 border-black bg-[#25D366] text-black px-3 py-1.5 hover:bg-black hover:text-[#25D366] transition-colors inline-flex items-center gap-1.5"
        data-testid="share-whatsapp-button"
        title="Bagikan ke WhatsApp"
      >
        <Share2 className="w-3.5 h-3.5" /> WhatsApp
      </button>
      <button
        type="button"
        onClick={copyText}
        className="border-2 border-black bg-white text-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-1.5"
        data-testid="copy-report-button"
        title="Salin laporan"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Tersalin" : "Salin"}
      </button>
      <button
        type="button"
        onClick={() => window.print()}
        className="border-2 border-black bg-white text-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-1.5"
        data-testid="print-report-button"
        title="Simpan sebagai PDF / cetak"
      >
        <Printer className="w-3.5 h-3.5" /> PDF
      </button>
    </div>
  );
}
